import { CircularProgress, FormControl, FormHelperText, Grid2, TextField } from '@mui/material';
import Autocomplete, { type AutocompleteRenderInputParams } from '@mui/material/Autocomplete';
import { debounce } from '@mui/material/utils';
import { type ReactNode, useCallback, useEffect, useState } from 'react';
import type { UseFormRegisterReturn } from 'react-hook-form';

interface AutoCompleteResponse<T> {
  result: T;
  count: number;
}

export type FetchOptionsFunction<T> = (
  input: string | number,
  filterParams?: Record<string, string | number>
) => Promise<AutoCompleteResponse<T[]>>;

interface AutoCompleteFormControlProps<T> {
  id: string;
  label: string;
  multipleLabelOption: string;
  singleLabelOption: string;
  getValues?: any;
  setValue?: any;
  fetchOptionsFunction: FetchOptionsFunction<T>;
  filters?: Record<string, string | number>;
  getOptionLabel: (option: T) => string;
  renderOption?: (option: T) => ReactNode;
  getOptionSelected: (option: T, value: T) => boolean;
  getOptionKey: (option: T) => string;
  defaultValue?: T | T[];
  onChangeValue?: (option: T | T[] | null) => void;
  Grid?: number;
  labelOption?: string;
  multiple?: boolean;
  error?: string | null;
  helperText?: string;
  registerProps?: UseFormRegisterReturn;
  setSelectedOption?: (option: T | T[] | null) => void;
  minInputLength?: number;
  required?: boolean;
  returnJoinedString?: boolean;
  disabled?: boolean;
  isLoading?: boolean;
  isSizeSmall?: boolean;
}

export function AutoCompleteFormControl<T>({
  id,
  label,
  registerProps,
  multiple = false,
  error,
  Grid,
  singleLabelOption,
  multipleLabelOption,
  getValues,
  setValue,
  getOptionLabel,
  renderOption,
  getOptionSelected,
  setSelectedOption,
  getOptionKey,
  onChangeValue,
  helperText,
  filters,
  fetchOptionsFunction,
  defaultValue,
  required,
  returnJoinedString = false,
  minInputLength = 3,
  disabled = false,
  isLoading = false,
  isSizeSmall = false
}: AutoCompleteFormControlProps<T>): JSX.Element {
  const [options, setOptions] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [key, setKey] = useState('');

  const fetchOptions = useCallback(
    debounce(async (search: string) => {
      setLoading(true);
      try {
        const response = await fetchOptionsFunction(search, filters);
        setOptions(response.result);
      } catch (error) {
        console.error(`Failed to fetch options: ${multipleLabelOption}`, error);
      } finally {
        setLoading(false);
      }
    }, 500),
    //todo:v2-changes
    [fetchOptionsFunction, setOptions, setLoading, filters]
  );

  useEffect(() => {
    setLoading(isLoading);
  }, [isLoading]);

  useEffect(() => {
    if (inputValue.length >= minInputLength) {
      fetchOptions(inputValue);
    } else {
      setOptions([]);
    }
    //todo:v2-changes
  }, [inputValue, fetchOptions]);

  useEffect(() => {
    if (defaultValue) {
      if (Array.isArray(defaultValue)) {
        setKey(defaultValue.map((value) => getOptionKey(value))?.join(''));
        setValue?.(
          id,
          returnJoinedString
            ? defaultValue.map((user: T) => getOptionKey(user))?.join(',')
            : defaultValue.map((user: T) => getOptionKey(user))
        );
        if (setSelectedOption) {
          setSelectedOption(defaultValue);
        }
      } else {
        setKey(getOptionKey(defaultValue));
        setValue?.(id, getOptionKey(defaultValue));
        if (setSelectedOption) {
          setSelectedOption(defaultValue);
        }
      }
    }
    //todo:v2-changes
  }, [defaultValue]);

  const controlElement = (
    <FormControl style={{ display: 'flex' }} error={error ? true : undefined}>
      <Autocomplete
        {...registerProps}
        onInputChange={(event, newInputValue, reason) => {
          if (newInputValue.length >= minInputLength && reason !== 'reset') {
            setInputValue(newInputValue);
            fetchOptions(newInputValue);
          }
        }}
        options={options}
        multiple={multiple}
        includeInputInList
        filterSelectedOptions
        filterOptions={(x) => x}
        disabled={disabled}
        getOptionLabel={getOptionLabel}
        renderOption={(props, option) => {
          return (
            <li {...props} key={getOptionKey(option)}>
              {renderOption ? renderOption(option) : getOptionLabel(option)}
            </li>
          );
        }}
        key={key}
        onChange={(_event, value) => {
          if (multiple) {
            setValue?.(
              id,
              Array.isArray(value)
                ? returnJoinedString
                  ? value.map((user: T) => getOptionKey(user))?.join(',')
                  : value.map((user: T) => getOptionKey(user))
                : returnJoinedString
                  ? ''
                  : []
            );
            if (setSelectedOption) {
              setSelectedOption(Array.isArray(value) ? value.map((user: T) => user) : ([] as T[]));
            }
            if (onChangeValue) {
              onChangeValue(value);
            }
          } else {
            if (setSelectedOption) {
              setSelectedOption(value);
            }
            if (onChangeValue) {
              onChangeValue(value as T);
            }
            setValue?.(id, value ? getOptionKey(value as T) : null);
          }
        }}
        isOptionEqualToValue={(option, value) => getOptionSelected(option, value)}
        loading={loading}
        defaultValue={defaultValue}
        loadingText={`Loading ${multipleLabelOption}...`}
        noOptionsText={
          inputValue.length < minInputLength
            ? `Type at least ${minInputLength} characters.`
            : `No ${multipleLabelOption} found.`
        }
        size={isSizeSmall ? 'small' : 'medium'}
        renderInput={(params: AutocompleteRenderInputParams) => (
          <TextField
            {...params}
            label={label}
            variant="outlined"
            required={required}
            helperText={
              helperText
                ? helperText
                : !inputValue || !getValues?.(id)
                  ? `Start typing a ${singleLabelOption}'s name`
                  : ''
            }
            slotProps={{
              input: {
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                )
              }
            }}
          />
        )}
      />
      {error && (
        <FormHelperText error id={`helper-text-${id}`}>
          {error}
        </FormHelperText>
      )}
    </FormControl>
  );

  return Grid ? <Grid2 size={{ xs: Grid }}>{controlElement}</Grid2> : controlElement;
}

export default AutoCompleteFormControl;
